import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchUnifiedCache } from "../hooks/useUnifiedCache";
import { useUserOptional } from "@/components/auth-provider";

/**
 * DataPrefetchProvider - Prefetches products and partners on app initialization
 *
 * This provider loads all products and partners into the unified global cache
 * when the user logs in. The data is then available immediately for all components
 * without needing additional API calls.
 *
 * Features:
 * - Loads data only when user is authenticated
 * - Runs once on mount
 * - Non-blocking (doesn't prevent app from rendering)
 * - Shares cache with all components via TanStack Query
 */
export function DataPrefetchProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const user = useUserOptional();

  useEffect(() => {
    // Only prefetch if user is authenticated
    if (!user) return;

    // Prefetch products and partners in the background
    // This won't block rendering but will populate the cache
    prefetchUnifiedCache(queryClient).catch((error) => {
      console.error("[DataPrefetchProvider] Failed to prefetch data:", error);
      // Don't throw - let the app continue, individual hooks will retry
    });
  }, [user, queryClient]);

  return <>{children}</>;
}
